/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * ConversationThread - Displays messages in a conversation with composer
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { ArrowLeft, User, Loader2, Archive, MoreVertical } from 'lucide-react'
import MessageBubble from './MessageBubble'
import MessageComposer from './MessageComposer'
import messagingService from '../../services/messagingService'
import toast from 'react-hot-toast'
import { createLogger } from '../../utils/logger'

const logger = createLogger('ConversationThread')

const ConversationThread = ({ conversationId, currentUserId, onBack, onConversationUpdated }) => {
  const [conversation, setConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [replyTo, setReplyTo] = useState(null)
  const [showMenu, setShowMenu] = useState(false)
  const messagesEndRef = useRef(null)
  const menuRef = useRef(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Load conversation
  const loadConversation = useCallback(async () => {
    try {
      setLoading(true)
      const response = await messagingService.getConversation(conversationId)
      if (response.success) {
        setConversation(response.conversation)
        setMessages(response.messages || [])
      }
    } catch (error) {
      logger.error('Error loading conversation:', error)
      toast.error('Failed to load conversation')
    } finally {
      setLoading(false)
    }
  }, [conversationId])

  useEffect(() => {
    if (conversationId) {
      loadConversation()
    }
  }, [conversationId, loadConversation])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = async (content, parentMessageId = null) => {
    try {
      setSending(true)
      const response = await messagingService.sendMessage(conversationId, content, parentMessageId)
      if (response.success) {
        setMessages((prev) => [...prev, response.message])
        onConversationUpdated?.()
      }
    } catch (error) {
      logger.error('Error sending message:', error)
      toast.error('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const handleDeleteMessage = (messageId) => {
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, isDeleted: true } : m)))
  }

  const handleArchive = async () => {
    try {
      await messagingService.archiveConversation(conversationId)
      toast.success('Conversation archived')
      onBack?.()
      onConversationUpdated?.()
    } catch (error) {
      toast.error('Failed to archive conversation')
    }
  }

  const getOtherParticipant = () => {
    if (!conversation) return null
    if (conversation.coachId === currentUserId) {
      return conversation.participant
    }
    return conversation.coach
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    })
  }

  // Group messages by date
  const groupMessagesByDate = () => {
    const groups = []
    let currentDate = null

    messages.forEach((message) => {
      const messageDate = new Date(message.createdAt).toDateString()
      if (messageDate !== currentDate) {
        currentDate = messageDate
        groups.push({
          date: message.createdAt,
          messages: [message]
        })
      } else {
        groups[groups.length - 1].messages.push(message)
      }
    })

    return groups
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-500 dark:text-gray-400">Conversation not found</p>
      </div>
    )
  }

  const otherParticipant = getOtherParticipant()
  const messageGroups = groupMessagesByDate()

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 md:hidden"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {otherParticipant?.firstName} {otherParticipant?.lastName}
            </h3>
            {conversation.subject && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{conversation.subject}</p>
            )}
            {conversation.regardingChild && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                About: {conversation.regardingChild.firstName}{' '}
                {conversation.regardingChild.lastName}
              </p>
            )}
          </div>
        </div>

        {/* Actions menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10 py-1">
              <button
                onClick={() => {
                  setShowMenu(false)
                  handleArchive()
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 whitespace-nowrap"
              >
                <Archive className="w-4 h-4 flex-shrink-0" />
                Archive conversation
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>No messages yet</p>
            <p className="text-sm">Send a message to start the conversation</p>
          </div>
        ) : (
          messageGroups.map((group, groupIndex) => (
            <div key={groupIndex}>
              {/* Date separator */}
              <div className="flex items-center justify-center my-4">
                <span className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(group.date)}
                </span>
              </div>

              {/* Messages for this date */}
              {group.messages.map((message) => (
                <div
                  key={message.id}
                  onDoubleClick={() => !message.isDeleted && setReplyTo(message)}
                >
                  <MessageBubble
                    message={message}
                    isOwn={message.senderId === currentUserId}
                    currentUserId={currentUserId}
                    onDelete={handleDeleteMessage}
                    showSender={message.senderId !== currentUserId}
                  />
                </div>
              ))}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Composer */}
      <MessageComposer
        onSend={handleSendMessage}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        disabled={sending || conversation.status === 'archived'}
        placeholder={
          conversation.status === 'archived' ? 'This conversation is archived' : 'Type a message...'
        }
      />
    </div>
  )
}

export default ConversationThread
